import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API from "../config/API";

function UserBedDetails() {
  const navigate = useNavigate();

  // =====================================================
  // STATES
  // =====================================================

  const [userRegisterId, setUserRegisterId] = useState(null);

  const [bedDetails, setBedDetails] = useState(null);

  const [currentBed, setCurrentBed] = useState(null);

  const [bedHistory, setBedHistory] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  // =====================================================
  // GET USER REGISTER ID
  // =====================================================

  useEffect(() => {
    const storedUserRegisterId = localStorage.getItem("userRegisterId");

    console.log("User Register ID:", storedUserRegisterId);

    if (!storedUserRegisterId) {
      setError("User registration ID not found. Please login again.");

      setLoading(false);

      return;
    }

    const registerId = Number(storedUserRegisterId);

    if (!Number.isInteger(registerId) || registerId <= 0) {
      setError("Invalid user registration ID.");

      setLoading(false);

      return;
    }

    setUserRegisterId(registerId);
  }, []);

  // =====================================================
  // GET USER BED DETAILS
  // =====================================================

  useEffect(() => {
    if (!userRegisterId) {
      return;
    }

    const fetchUserBedDetails = async () => {
      try {
        setLoading(true);

        setError("");

        console.log("Fetching bed details for:", userRegisterId);

        const response = await axios.get(
          `${API.V2}/usersDashboard/getUserBedDetailsByRegisterId`,
          {
            params: {
              userRegisterId: userRegisterId,
            },
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        console.log("User Bed Details Response:", response.data);

        // =================================================
        // SUCCESS
        // =================================================

        if (response.data?.statusCode === 200 && response.data?.data) {
          const data = response.data.data;

          setBedDetails(data);

          // =================================================
          // HANDLE DIFFERENT RESPONSE STRUCTURES
          // =================================================

          /*
           * Case 1:
           * data = {
           *    currentBed: {...},
           *    history: [...]
           * }
           */

          if (!Array.isArray(data) && data.currentBed) {
            setCurrentBed(data.currentBed);

            setBedHistory(Array.isArray(data.history) ? data.history : []);
          } else if (!Array.isArray(data) && data.currentBedDetails) {
            /*
             * Case 2:
             * data = {
             *    currentBedDetails: {...},
             *    bedHistory: [...]
             * }
             */
            setCurrentBed(data.currentBedDetails);

            setBedHistory(
              Array.isArray(data.bedHistory) ? data.bedHistory : [],
            );
          } else if (Array.isArray(data)) {
            /*
             * Case 3:
             * data = array
             *
             * We assume the first record is current
             * and remaining records are history.
             */
            if (data.length > 0) {
              setCurrentBed(data[0]);

              setBedHistory(data.slice(1));
            } else {
              setCurrentBed(null);

              setBedHistory([]);
            }
          } else {
            /*
             * Case 4:
             * data itself is one current-bed object
             */
            setCurrentBed(data);

            setBedHistory(Array.isArray(data.history) ? data.history : []);
          }
        } else {
          setError(response.data?.message || "Bed details not found.");
        }
      } catch (error) {
        console.error("Get User Bed Details Error:", error);

        setError(
          error.response?.data?.message || "Unable to fetch bed details.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUserBedDetails();
  }, [userRegisterId]);

  // =====================================================
  // BACK TO DASHBOARD
  // =====================================================

  const handleBack = () => {
    navigate("/userDashboard");
  };

  // =====================================================
  // UPDATE BED
  // =====================================================

  const handleUpdate = () => {
    if (!currentBed) {
      return;
    }

    console.log("Update Bed:", currentBed);

    navigate("/updateUserBedDetails", {
      state: {
        userRegisterId: userRegisterId,
        bedDetails: currentBed,
      },
    });
  };

  // =====================================================
  // FORMAT DATE
  // =====================================================

  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    try {
      return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      return date;
    }
  };

  // =====================================================
  // GET VALUE FROM MULTIPLE POSSIBLE FIELD NAMES
  // =====================================================

  const getValue = (obj, ...keys) => {
    if (!obj) {
      return "-";
    }

    for (const key of keys) {
      if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") {
        return obj[key];
      }
    }

    return "-";
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingCard}>
          <div style={styles.loadingIcon}>⏳</div>

          <h2 style={styles.loadingTitle}>Loading Bed Details...</h2>

          <p style={styles.loadingText}>
            Please wait while we fetch your current and previous bed details.
          </p>
        </div>
      </div>
    );
  }

  // =====================================================
  // ERROR
  // =====================================================

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.errorCard}>
          <div style={styles.errorIcon}>⚠️</div>

          <h2 style={styles.errorTitle}>Unable to Load Bed Details</h2>

          <p style={styles.errorText}>{error}</p>

          <div style={styles.errorButtons}>
            <button
              type="button"
              onClick={handleBack}
              style={styles.backButton}
            >
              ← Back to Dashboard
            </button>

            <button
              type="button"
              onClick={() => window.location.reload()}
              style={styles.retryButton}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =====================================================
  // MAIN PAGE
  // =====================================================

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* =================================================
            HEADER
        ================================================= */}

        <div style={styles.header}>
          <div>
            <button type="button" onClick={handleBack} style={styles.backLink}>
              ← Dashboard
            </button>

            <h1 style={styles.title}>Bed Details</h1>

            <p style={styles.subtitle}>
              View your current and previous bed details
            </p>
          </div>

          <div style={styles.registerBadge}>
            <span>Registration ID</span>

            <strong>{userRegisterId}</strong>
          </div>
        </div>

        {/* =================================================
            CURRENT BED DETAILS
        ================================================= */}

        <div style={styles.sectionCard}>
          <div style={styles.sectionHeader}>
            <div>
              <h2 style={styles.sectionTitle}>🛏️ Current Bed Details</h2>

              <p style={styles.sectionSubtitle}>
                Your currently assigned bed information
              </p>
            </div>

            {currentBed && <div style={styles.currentBadge}>CURRENT</div>}
          </div>

          {currentBed ? (
            <>
              <div style={styles.detailsGrid}>
                <DetailItem
                  label="Floor"
                  value={getValue(currentBed, "floorName", "floor")}
                  icon="🏢"
                />

                <DetailItem
                  label="Room"
                  value={getValue(currentBed, "roomName", "room")}
                  icon="🚪"
                />

                <DetailItem
                  label="Bed"
                  value={getValue(
                    currentBed,
                    "userBedName",
                    "bedNumber",
                    "bed",
                  )}
                  icon="🛏️"
                />

                <DetailItem
                  label="Room Sharing"
                  value={getValue(currentBed, "roomSharingType", "sharingType")}
                  icon="👥"
                />

                <DetailItem
                  label="Joining Date"
                  value={formatDate(
                    getValue(
                      currentBed,
                      "joiningDate",
                      "joinDate",
                      "createdDate",
                    ),
                  )}
                  icon="📅"
                />

                <DetailItem
                  label="Stay Type"
                  value={getValue(currentBed, "stayType", "paymentType")}
                  icon="📋"
                />

                <DetailItem
                  label="Bed Cost"
                  value={
                    getValue(
                      currentBed,
                      "bedCost",
                      "userBedCost",
                      "actualPrice",
                    ) !== "-"
                      ? `₹ ${getValue(
                          currentBed,
                          "bedCost",
                          "userBedCost",
                          "actualPrice",
                        )}`
                      : "-"
                  }
                  icon="💰"
                />

                <DetailItem
                  label="Payment Status"
                  value={getValue(currentBed, "paymentStatus")}
                  icon="💳"
                />
              </div>

              {/* =================================================
                  UPDATE BUTTON
              ================================================= */}

              <div style={styles.actionSection}>
                <button
                  type="button"
                  onClick={handleUpdate}
                  style={styles.updateButton}
                >
                  ✏️ Update Bed Details
                </button>
              </div>
            </>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🛏️</div>

              <h3>No Current Bed Assigned</h3>

              <p>There is currently no active bed assigned to your account.</p>
            </div>
          )}
        </div>

        {/* =================================================
            BED HISTORY
        ================================================= */}

        <div style={styles.sectionCard}>
          <div style={styles.sectionHeader}>
            <div>
              <h2 style={styles.sectionTitle}>📜 Bed History</h2>

              <p style={styles.sectionSubtitle}>
                Your previous bed assignments
              </p>
            </div>

            <div style={styles.historyCount}>
              {bedHistory.length} Record
              {bedHistory.length !== 1 ? "s" : ""}
            </div>
          </div>

          {bedHistory.length > 0 ? (
            <div style={styles.historyTableWrapper}>
              <table style={styles.historyTable}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>

                    <th style={styles.th}>Floor</th>

                    <th style={styles.th}>Room</th>

                    <th style={styles.th}>Bed</th>

                    <th style={styles.th}>Joining Date</th>

                    <th style={styles.th}>Leaving Date</th>

                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {bedHistory.map((bed, index) => (
                    <tr key={bed.bedId || bed.id || index}>
                      <td style={styles.td}>{index + 1}</td>

                      <td style={styles.td}>
                        {getValue(bed, "floorName", "floor")}
                      </td>

                      <td style={styles.td}>
                        {getValue(bed, "roomName", "room")}
                      </td>

                      <td style={styles.td}>
                        {getValue(bed, "bedName", "bedNumber", "bed")}
                      </td>

                      <td style={styles.td}>
                        {formatDate(
                          getValue(
                            bed,
                            "joiningDate",
                            "joinDate",
                            "createdDate",
                          ),
                        )}
                      </td>

                      <td style={styles.td}>
                        {formatDate(
                          getValue(
                            bed,
                            "leavingDate",
                            "endDate",
                            "updatedDate",
                          ),
                        )}
                      </td>

                      <td style={styles.td}>
                        <span style={styles.historyStatus}>
                          {getValue(bed, "status", "bedStatus")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={styles.noHistory}>
              <div style={styles.noHistoryIcon}>📋</div>

              <h3>No Bed History</h3>

              <p>No previous bed assignment records were found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================
// DETAIL ITEM COMPONENT
// =====================================================

function DetailItem({ label, value, icon }) {
  return (
    <div style={styles.detailItem}>
      <div style={styles.detailIcon}>{icon}</div>

      <div style={styles.detailContent}>
        <span style={styles.detailLabel}>{label}</span>

        <span style={styles.detailValue}>{value}</span>
      </div>
    </div>
  );
}

// =====================================================
// STYLES
// =====================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg,#eff6ff,#f8fafc,#e0f2fe)",
    padding: "30px 20px",
    fontFamily: "Segoe UI, Arial, sans-serif",
    boxSizing: "border-box",
  },

  container: {
    width: "100%",
    maxWidth: "1150px",
    margin: "0 auto",
  },

  header: {
    background: "#ffffff",
    borderRadius: "20px",
    padding: "25px 30px",
    marginBottom: "25px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "20px",
    boxShadow: "0 10px 30px rgba(15,23,42,.08)",
  },

  backLink: {
    border: "none",
    background: "transparent",
    padding: 0,
    color: "#2563eb",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
    marginBottom: "10px",
  },

  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: "30px",
    fontWeight: "800",
  },

  subtitle: {
    margin: "7px 0 0",
    color: "#64748b",
    fontSize: "14px",
  },

  registerBadge: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "12px",
    padding: "12px 18px",
    display: "flex",
    flexDirection: "column",
    gap: "3px",
    minWidth: "130px",
  },

  sectionCard: {
    background: "#ffffff",
    borderRadius: "20px",
    padding: "28px",
    marginBottom: "25px",
    boxShadow: "0 10px 30px rgba(15,23,42,.08)",
  },

  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "15px",
    marginBottom: "25px",
  },

  sectionTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "22px",
    fontWeight: "800",
  },

  sectionSubtitle: {
    margin: "5px 0 0",
    color: "#64748b",
    fontSize: "13px",
  },

  currentBadge: {
    background: "#dcfce7",
    color: "#166534",
    borderRadius: "20px",
    padding: "7px 14px",
    fontSize: "11px",
    fontWeight: "800",
  },

  historyCount: {
    background: "#f1f5f9",
    color: "#475569",
    borderRadius: "20px",
    padding: "7px 14px",
    fontSize: "12px",
    fontWeight: "700",
  },

  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))",
    gap: "15px",
  },

  detailItem: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "18px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
  },

  detailIcon: {
    width: "45px",
    height: "45px",
    borderRadius: "12px",
    background: "#eff6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
    flexShrink: 0,
  },

  detailContent: {
    minWidth: 0,
  },

  detailLabel: {
    display: "block",
    color: "#64748b",
    fontSize: "12px",
    fontWeight: "600",
    marginBottom: "5px",
  },

  detailValue: {
    display: "block",
    color: "#0f172a",
    fontSize: "15px",
    fontWeight: "800",
    wordBreak: "break-word",
  },

  actionSection: {
    marginTop: "25px",
    paddingTop: "20px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "flex-end",
  },

  updateButton: {
    border: "none",
    borderRadius: "10px",
    padding: "12px 22px",
    background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 5px 15px rgba(37,99,235,.25)",
  },

  historyTableWrapper: {
    width: "100%",
    overflowX: "auto",
  },

  historyTable: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "800px",
  },

  th: {
    textAlign: "left",
    padding: "14px 12px",
    background: "#f8fafc",
    color: "#475569",
    fontSize: "12px",
    fontWeight: "800",
    borderBottom: "1px solid #e2e8f0",
  },

  td: {
    padding: "15px 12px",
    color: "#334155",
    fontSize: "13px",
    borderBottom: "1px solid #e2e8f0",
  },

  historyStatus: {
    display: "inline-block",
    background: "#f1f5f9",
    color: "#475569",
    borderRadius: "15px",
    padding: "5px 10px",
    fontSize: "11px",
    fontWeight: "700",
  },

  emptyState: {
    textAlign: "center",
    padding: "35px 20px",
    color: "#64748b",
  },

  emptyIcon: {
    fontSize: "45px",
    marginBottom: "10px",
  },

  noHistory: {
    textAlign: "center",
    padding: "30px",
    background: "#f8fafc",
    borderRadius: "14px",
    color: "#64748b",
  },

  noHistoryIcon: {
    fontSize: "40px",
    marginBottom: "8px",
  },

  loadingCard: {
    maxWidth: "450px",
    margin: "100px auto",
    padding: "40px",
    background: "#ffffff",
    borderRadius: "20px",
    textAlign: "center",
    boxShadow: "0 20px 50px rgba(15,23,42,.12)",
  },

  loadingIcon: {
    fontSize: "45px",
    marginBottom: "15px",
  },

  loadingTitle: {
    margin: 0,
    color: "#0f172a",
  },

  loadingText: {
    color: "#64748b",
    fontSize: "14px",
  },

  errorCard: {
    maxWidth: "500px",
    margin: "100px auto",
    padding: "40px",
    background: "#ffffff",
    borderRadius: "20px",
    textAlign: "center",
    boxShadow: "0 20px 50px rgba(15,23,42,.12)",
  },

  errorIcon: {
    fontSize: "45px",
    marginBottom: "15px",
  },

  errorTitle: {
    margin: 0,
    color: "#991b1b",
  },

  errorText: {
    color: "#64748b",
    fontSize: "14px",
    margin: "12px 0 25px",
  },

  errorButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
  },

  backButton: {
    border: "none",
    borderRadius: "10px",
    padding: "11px 18px",
    background: "#e2e8f0",
    color: "#334155",
    fontWeight: "700",
    cursor: "pointer",
  },

  retryButton: {
    border: "none",
    borderRadius: "10px",
    padding: "11px 18px",
    background: "#2563eb",
    color: "#ffffff",
    fontWeight: "700",
    cursor: "pointer",
  },
};

export default UserBedDetails;
